import type { DataGrid } from '../types'

/** 布局宽度：含纵向滚动条占位，使列宽/行线可铺满到 grid 右边缘 */
export function resolveGridLayoutWidth(element?: HTMLElement | null): number {
  if (!element) {
    return 0
  }
  const width = element.offsetWidth
  return width > 0 ? width : element.clientWidth
}

export function resolveGridContainerWidth(
  $grid: Pick<DataGrid, 'bodyScrollRef' | 'bodyRef'>
): number {
  return resolveGridLayoutWidth(
    $grid.bodyScrollRef?.value ?? $grid.bodyRef?.value
  )
}

/** 表体/表头/表尾内容区宽度：横向溢出时贴合列宽总和，否则铺满 layout 宽度 */
export function resolveGridBodyContentWidth(
  tableWidth: number,
  layoutWidth: number,
  overflowX: boolean
): number {
  if (tableWidth <= 0) {
    return layoutWidth > 0 ? layoutWidth : 0
  }
  if (overflowX || layoutWidth <= 0) {
    return tableWidth
  }
  return Math.max(tableWidth, layoutWidth)
}

export interface MeasureGridRenderedContentWidthOptions {
  bodyScrollEl?: HTMLElement | null
  headerScrollEl?: HTMLElement | null
}

function sumVisibleCellWidths(rowEl: HTMLElement) {
  const cells = rowEl.querySelectorAll<HTMLElement>(
    '.z-grid__cell:not(.fixed--hidden), .z-grid__header-cell:not(.fixed--hidden)'
  )
  return Array.from(cells).reduce((sum, cell) => sum + cell.offsetWidth, 0)
}

/** 渲染后实测内容宽度，修正列宽模型与 DOM（padding/表头同步）偏差 */
export function measureGridRenderedContentWidth(
  options: MeasureGridRenderedContentWidthOptions
): number {
  let maxWidth = 0
  const { bodyScrollEl, headerScrollEl } = options

  if (bodyScrollEl) {
    maxWidth = Math.max(maxWidth, bodyScrollEl.scrollWidth)
    const rowEl = bodyScrollEl.querySelector<HTMLElement>('.z-grid__row')
    if (rowEl) {
      maxWidth = Math.max(
        maxWidth,
        rowEl.offsetWidth,
        rowEl.scrollWidth,
        sumVisibleCellWidths(rowEl)
      )
    }
  }

  const headerRowEl = headerScrollEl?.querySelector<HTMLElement>('.z-grid__row')
  if (headerRowEl) {
    maxWidth = Math.max(
      maxWidth,
      headerRowEl.offsetWidth,
      headerRowEl.scrollWidth,
      sumVisibleCellWidths(headerRowEl)
    )
  }

  return maxWidth
}

export function resolveEffectiveGridContentWidth(
  modelWidth: number,
  renderedWidth: number
): number {
  if (modelWidth <= 0) {
    return renderedWidth > 0 ? renderedWidth : 0
  }
  if (renderedWidth <= 0) {
    return modelWidth
  }
  return Math.max(modelWidth, renderedWidth)
}

/** 纵向滚动条占用宽度。横溢时表头/表尾/横条需让出该值，才能与表体 clientWidth 对齐 */
export function resolveScrollbarGutter(element?: HTMLElement | null): number {
  if (!element) {
    return 0
  }
  return Math.max(0, element.offsetWidth - element.clientWidth)
}

/**
 * 横向最大 scrollLeft。必须用 clientWidth（不含纵向滚动条）。
 * 不可给滚动容器设 padding-right：clientWidth 含 padding，上限不会变。
 */
export function resolveMaxGridScrollLeft(
  contentWidth: number,
  clientWidth: number
): number {
  return Math.max(0, contentWidth - clientWidth)
}
